import { ApiError } from '@/api/client';
import type { Vacancy } from '@/api/schema';
import { Alert } from '@/components/feedback/Alert';
import { useToast } from '@/components/feedback/Toast';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';

import { useBulkDeleteVacancies } from '../api';
import styles from '../pages/CareersPage.module.css';

export function VacancyBulkDeleteDialog({
  jobs,
  onClose,
  onSuccess,
}: {
  jobs: Vacancy[];
  onClose: () => void;
  onSuccess: (affected: number) => void;
}) {
  const mutation = useBulkDeleteVacancies();
  const toast = useToast();
  const close = () => {
    if (!mutation.isPending) onClose();
  };
  const countLabel = `${jobs.length} ${jobs.length === 1 ? 'vacancy' : 'vacancies'}`;

  return (
    <Modal
      open
      title={`Delete ${countLabel}?`}
      description="The selected vacancies will be removed from the vacancy list and public Jobs page. Existing applications and candidate records will be kept."
      tone="danger"
      onClose={close}
      footer={
        <>
          <Button variant="ghost" disabled={mutation.isPending} onClick={close}>
            Cancel
          </Button>
          <Button
            variant="danger"
            loading={mutation.isPending}
            onClick={() => {
              mutation.mutate(
                { vacancy_ids: jobs.map((job) => job.id) },
                {
                  onSuccess: ({ affected }) => {
                    toast.success(
                      `${affected} ${affected === 1 ? 'vacancy' : 'vacancies'} deleted.`,
                    );
                    onSuccess(affected);
                    onClose();
                  },
                },
              );
            }}
          >
            Delete {countLabel}
          </Button>
        </>
      }
    >
      <ul className={styles.deleteSelection}>
        {jobs.map((job) => (
          <li key={job.id}>
            <strong>{job.title}</strong>
            <span>{job.country}</span>
          </li>
        ))}
      </ul>
      {mutation.isError ? (
        <Alert tone="danger">
          {mutation.error instanceof ApiError
            ? mutation.error.message
            : 'The vacancies could not be deleted. Please try again.'}
        </Alert>
      ) : null}
    </Modal>
  );
}
